import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PolicyForm from './PolicyForm';
import Logo from './Logo';
import ZeroToleranceInput from './ZeroToleranceInput';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TextEvaluator from './TextEvaluator';

const StepHeading = ({ number, text }) => (
  <Typography 
    variant="h3" 
    gutterBottom 
    sx={{ 
      mt: 4, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      gap: 2 
    }}
  >
    <Box
      component="span"
      sx={{
        color: 'primary.main',
        fontWeight: 800,
      }}
    >
      Step {number}:
    </Box>
    {text}
  </Typography>
);

const LandingPage = ({ 
  onProtectSubmit, 
  onPolicyUpdate, 
  onZeroToleranceUpdate,
  result, 
  loading, 
  error 
}) => {
  const codeString = `# Initialize the Ceberus client with your policy
ceberus_client = CeberusClient(policy)

# Check text for policy violations
result = ceberus_client.check_text("Input text")`;

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
          textAlign: 'center',
          gap: 2
        }}
      >
        <Logo />

        {/* Header */}
        <Typography variant="h2" component="h1" sx={{ mb: 2 }}>
          One API for text <Box component="span" sx={{ color: 'primary.main' }}>safety.</Box>
        </Typography>

        {/* Subheader */}
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mb: 3 }}>
          Ceberus has you covered to keep your apps, chat rooms, forums, or LLM text generation safe
        </Typography>

        {/* Code snippet */}
        <Box sx={{ width: '100%', maxWidth: 800, borderRadius: 2, overflow: 'hidden' }}>
          <SyntaxHighlighter 
            language="python"
            style={vscDarkPlus}
            customStyle={{
              padding: '20px',
              borderRadius: '8px',
              margin: 0,
              backgroundColor: '#1E1E1E',
              fontSize: '14px',
            }}
            wrapLines={true}
            showLineNumbers={false}
          >
            {codeString}
          </SyntaxHighlighter>
        </Box>

        {/* Demo heading */}
        <Typography 
          variant="h2" 
          sx={{ 
            mt: 4,
            mb: 2,
            fontSize: '3.5rem',
            fontWeight: 800,
            background: 'linear-gradient(45deg, #4161FF, #80B3FF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Demo
        </Typography>

        {/* Steps */}
        <Box sx={{ width: '100%' }}>
          <StepHeading number="1" text="Craft your policy" />
          <PolicyForm onUpdate={onPolicyUpdate} />
          
          <StepHeading number="2" text="Refine no-tolerance terms" />
          <ZeroToleranceInput onUpdate={onZeroToleranceUpdate} />
          
          <StepHeading number="3" text="Try calling the API" />
          <TextEvaluator 
            onSubmit={onProtectSubmit}
            loading={loading}
            error={error}
            result={result}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default LandingPage; 